import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import { providePerformance, getPerformance } from '@angular/fire/performance';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi, withInterceptors } from '@angular/common/http';
import { provideI18n } from '@shared';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from '@shared';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { ApiConfigService, AuthInterceptor, languageInterceptor, OfflineInterceptor, OfflineCacheService, NotificationsService, PRODUCTS_EXCLUDE_EXPIRED } from '@shared';
import { LanguageService } from './services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { NoOfflineQueueInterceptor } from './interceptors/no-offline-queue.interceptor';
import { ConnectivityInterceptor } from './interceptors/connectivity.interceptor';
import { NotificationsDisabledService } from './services/notifications-disabled.service';

// Initialize API configuration
function initializeApiConfig() {
  return () => {
    const apiConfig = inject(ApiConfigService);
    
    // Detect platform and use appropriate API URL
    let apiUrl = environment.apiUrl;
    let wsUrl = environment.wsUrl;
    
    // For Android, localhost doesn't work - need to use machine's IP or emulator IP
    if (typeof window !== 'undefined') {
      try {
        // Check if Capacitor is available
        const Capacitor = (window as any).Capacitor;
        if (Capacitor && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
          // Android Emulator uses 10.0.2.2 to access host's localhost
          // Physical device needs your machine's local IP address
          const isEmulator = window.location.hostname === '10.0.2.2' || 
                            window.location.hostname === 'localhost';
          
          if (isEmulator) {
            // Android Emulator
            apiUrl = environment.apiUrl.replace('localhost', '10.0.2.2');
            wsUrl = environment.wsUrl.replace('localhost', '10.0.2.2');
          } else {
            // Android Physical Device - use your machine's local IP (same Wi‑Fi)
            const localIp = (environment as any).localNetworkIp || '10.0.0.75';
            apiUrl = environment.apiUrl.replace('localhost', localIp);
            wsUrl = environment.wsUrl.replace('localhost', localIp);
          }
        }
      } catch (e) {
        // If Capacitor detection fails, check user agent as fallback
        const userAgent = window.navigator.userAgent || '';
        if (userAgent.includes('Android') && !userAgent.includes('Chrome')) {
          // Likely Android device - use local IP
          const localIp = (environment as any).localNetworkIp || '10.0.0.75';
          apiUrl = environment.apiUrl.replace('localhost', localIp);
          wsUrl = environment.wsUrl.replace('localhost', localIp);
        }
      }
    }
    
    apiConfig.setApiUrl(apiUrl);
    apiConfig.setWsUrl(wsUrl);
    
    return Promise.resolve();
  };
}

/**
 * Initialize language service and load translations
 * 
 * ISSUE RESOLVED:
 * When English is set as the default language, ngx-translate's TranslateService
 * may skip loading the English translation file, assuming English is the base language.
 * This causes translation keys to display literally instead of translated text.
 * 
 * SOLUTION:
 * For English, we manually load the translation file via HTTP and set it explicitly.
 * For other languages, we rely on TranslateService's normal HTTP loader.
 */
function initializeLanguage() {
  return async () => {
    const languageService = inject(LanguageService);
    const translateService = inject(TranslateService);
    const http = inject(HttpClient);
    
    // Get the language to use (from LanguageService or default to 'en')
    const currentLang = languageService.getCurrentLanguage() || 'en';
    
    // Set default language
    translateService.setDefaultLang('en');
    
    // For English (default language), manually load translations
    // TranslateService may skip loading the default language file
    if (currentLang === 'en') {
      try {
        const translations = await firstValueFrom(http.get<Record<string, any>>('/assets/i18n/en.json'));
        translateService.setTranslation('en', translations, true);
      } catch (error) {
        // Fallback: use TranslateService's normal loader
        // This ensures the app still works even if manual load fails
        translateService.use('en');
      }
    } else {
      // For other languages, use normal HTTP loader
      translateService.use(currentLang);
    }
    
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([languageInterceptor])
    ),
    // Track backend reachability (online-but-server-down) for a global banner on all pages.
    { provide: HTTP_INTERCEPTORS, useClass: ConnectivityInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // Option A: user app must NOT queue non-GET requests when offline (orders/payments/etc).
    // This interceptor marks non-GETs so the shared OfflineInterceptor fails fast instead of returning 202 "Queued".
    { provide: HTTP_INTERCEPTORS, useClass: NoOfflineQueueInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptor, multi: true },
    provideI18n(),
    provideIonicAngular({
      mode: 'ios', // Use iOS mode for native feel, can be 'md' for Material Design
      rippleEffect: true,
      hardwareBackButton: true,
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Hide expired SKUs from customer catalog (vendor detail, search, last bought, etc.).
    { provide: PRODUCTS_EXCLUDE_EXPIRED, useValue: true },
    // In-app notification *list* is disabled (no bell/history API). Web push FCM still works via
    // FCMService + ApiNotificationsService.registerFCMToken in UserNotificationSoundService.
    { provide: NotificationsService, useClass: NotificationsDisabledService },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApiConfig,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        return () => {
          const cacheService = inject(OfflineCacheService);
          cacheService.initialize();
          return Promise.resolve();
        };
      },
      multi: true,
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    provideMessaging(() => getMessaging()),
    provideFunctions(() => getFunctions()),
    providePerformance(() => {
      const perf = getPerformance();
      return perf;
    }),
  ],
};
